import React, { useState } from 'react';
import { Search, Copy, Trash2 } from 'lucide-react';

const JsonPathTesterTool = () => {
  const [json, setJson] = useState('');
  const [path, setPath] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const evaluatePath = (obj, pathStr) => {
    if (!pathStr) return obj;
    
    // Handle root reference
    if (pathStr === '$') return obj;
    
    // Remove leading $ and split
    const cleanPath = pathStr.replace(/^\$\.?/, '');
    if (!cleanPath) return obj;

    const parts = [];
    let current = '';
    let inBracket = false;

    for (const char of cleanPath) {
      if (char === '[') {
        if (current) parts.push(current);
        current = '';
        inBracket = true;
      } else if (char === ']') {
        parts.push(current);
        current = '';
        inBracket = false;
      } else if (char === '.' && !inBracket) {
        if (current) parts.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    if (current) parts.push(current);

    let value = obj;
    for (const part of parts) {
      if (value === null || value === undefined) return undefined;
      
      // Handle array index
      if (/^\d+$/.test(part)) {
        value = value[parseInt(part)];
      }
      // Handle wildcard
      else if (part === '*') {
        if (Array.isArray(value)) {
          return value;
        }
        return Object.values(value);
      }
      // Handle slice
      else if (part.includes(':')) {
        const [start, end] = part.split(':').map(n => n ? parseInt(n) : undefined);
        if (Array.isArray(value)) {
          return value.slice(start, end);
        }
      }
      // Handle quoted string
      else if ((part.startsWith("'") && part.endsWith("'")) || (part.startsWith('"') && part.endsWith('"'))) {
        value = value[part.slice(1, -1)];
      }
      else {
        value = value[part];
      }
    }
    return value;
  };

  const testPath = () => {
    setError('');
    setResult(null);

    if (!json.trim()) {
      setError('Please enter JSON data');
      return;
    }

    try {
      const parsed = JSON.parse(json);
      
      if (!path.trim()) {
        setResult(parsed);
        return;
      }

      const value = evaluatePath(parsed, path);
      setResult(value);
    } catch (e) {
      if (e instanceof SyntaxError) {
        setError('Invalid JSON: ' + e.message);
      } else {
        setError('Path error: ' + e.message);
      }
    }
  };

  const copyResult = () => {
    const text = typeof result === 'object' ? JSON.stringify(result, null, 2) : String(result);
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const loadSample = () => {
    const sample = {
      store: {
        name: "My Store",
        books: [
          { title: "Book 1", price: 10, author: "Author A" },
          { title: "Book 2", price: 15, author: "Author B" },
          { title: "Book 3", price: 20, author: "Author A" }
        ],
        location: {
          city: "New York",
          country: "USA"
        }
      }
    };
    setJson(JSON.stringify(sample, null, 2));
    setPath('$.store.books[0].title');
  };

  const examples = [
    { path: '$', desc: 'Root element' },
    { path: '$.store.name', desc: 'Nested property' },
    { path: '$.store.books[0]', desc: 'First array element' },
    { path: '$.store.books[*].title', desc: 'All titles' },
    { path: '$.store.books[0:2]', desc: 'Array slice' },
    { path: '$.store.location.city', desc: 'Deep nesting' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-gray-300">JSON Data</label>
            <button
              onClick={loadSample}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Load Sample
            </button>
          </div>
          <textarea
            value={json}
            onChange={(e) => setJson(e.target.value)}
            placeholder='{"key": "value"}'
            className="w-full h-64 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono text-sm placeholder-gray-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Result</label>
          <div className="w-full h-64 bg-gray-800 border border-gray-700 rounded-lg p-4 overflow-auto">
            {error ? (
              <div className="text-red-400 text-sm">{error}</div>
            ) : result !== null ? (
              <pre className="text-green-400 text-sm whitespace-pre-wrap">
                {typeof result === 'object' ? JSON.stringify(result, null, 2) : String(result)}
              </pre>
            ) : (
              <span className="text-gray-500 text-sm">Result will appear here...</span>
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">JSONPath Expression</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="$.store.books[0].title"
            className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white font-mono placeholder-gray-500 focus:border-blue-500"
            onKeyDown={(e) => e.key === 'Enter' && testPath()}
          />
          <button
            onClick={testPath}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Search size={18} />
            Test
          </button>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Quick Examples</h3>
        <div className="flex flex-wrap gap-2">
          {examples.map((ex) => (
            <button
              key={ex.path}
              onClick={() => setPath(ex.path)}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-sm text-gray-300 transition-colors"
              title={ex.desc}
            >
              {ex.path}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={copyResult}
          disabled={result === null}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy Result'}
        </button>
        <button
          onClick={() => { setJson(''); setPath(''); setResult(null); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear All
        </button>
      </div>
    </div>
  );
};

export default JsonPathTesterTool;
